import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import SEO from '../components/SEO';
import { Label } from '../components/ui/label';
import { companyInfo } from '../data/mockData';
import { Mail, Phone, MapPin, MessageCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent } from '../analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    country: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/inquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          inquiry_type: 'contact',
          name: formData.name,
          email: formData.email,
          company: formData.company,
          phone: formData.phone,
          country: formData.country,
          message: formData.message
        }),
      });

      if (response.ok) {
        toast.success('Thank you for your inquiry! We will contact you shortly.');
        trackEvent('form_submit', { form_type: 'contact_inquiry' });
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          country: '',
          message: ''
        });
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppClick = () => {
    const message = 'Hello, I am interested in learning more about Trustar Industrial Chemicals products.';
    const whatsappUrl = `https://wa.me/${companyInfo.contact.whatsapp.replace(/\s+/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div>
      <SEO title="Contact Us" description="Contact Trustar Industrial Chemicals for TiO2 and specialty pigment inquiries. Email us at sales@trustarchem.com." path="/contact" />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-blue-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold mb-4">Contact Us</h1>
          <p className="text-xl text-gray-700">
            Get in touch with our team for inquiries and support
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-bold mb-6">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="company">Company Name</Label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleChange}
                      className="mt-1"
                      placeholder="Your company"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="mt-1"
                      placeholder="+971 XX XXX XXXX"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    name="country"
                    type="text"
                    value={formData.country}
                    onChange={handleChange}
                    className="mt-1"
                    placeholder="Your country"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="mt-1"
                    rows={6}
                    placeholder="Tell us about your requirements..."
                  />
                </div>

                <Button type="submit" size="lg" className="w-full md:w-auto" disabled={isSubmitting} data-testid="contact-form-submit">
                  {isSubmitting ? (
                    <>Submitting...</>
                  ) : (
                    <>
                      <Send size={18} className="mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>

              {/* WhatsApp CTA */}
              <div className="mt-8 p-6 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start">
                  <MessageCircle className="text-green-600 mt-1 mr-3 flex-shrink-0" size={24} />
                  <div className="flex-1">
                    <h3 className="font-semibold mb-2">Quick Response via WhatsApp</h3>
                    <p className="text-sm text-gray-700 mb-3">
                      Need immediate assistance? Reach us directly on WhatsApp for faster response.
                    </p>
                    <Button
                      onClick={handleWhatsAppClick}
                      className="bg-green-600 hover:bg-green-700"
                      size="sm"
                    >
                      <MessageCircle size={16} className="mr-2" />
                      Chat on WhatsApp
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-2xl font-bold mb-6">Contact Information</h2>
              
              <Card className="mb-6 border-none shadow-sm">
                <CardContent className="p-6">
                  <div className="space-y-6">
                    <div className="flex items-start">
                      <div className="bg-blue-50 p-3 rounded-lg mr-4">
                        <Mail className="text-primary" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">Email</h3>
                        <a
                          href={`mailto:${companyInfo.contact.email}`}
                          className="text-primary hover:underline"
                        >
                          {companyInfo.contact.email}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="bg-blue-50 p-3 rounded-lg mr-4">
                        <Phone className="text-primary" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">Phone / WhatsApp</h3>
                        <p className="text-gray-700">{companyInfo.contact.phone}</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="bg-blue-50 p-3 rounded-lg mr-4">
                        <MapPin className="text-primary" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">Address</h3>
                        <address className="text-gray-700 not-italic text-sm leading-relaxed">
                          {companyInfo.legalName}<br />
                          {companyInfo.address.line1}<br />
                          {companyInfo.address.line2}<br />
                          {companyInfo.address.line3}<br />
                          {companyInfo.address.poBox}<br />
                          {companyInfo.address.city}, {companyInfo.address.country}
                        </address>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Business Hours */}
              <Card className="border-none shadow-sm">
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-4">Business Hours</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sunday - Thursday</span>
                      <span className="font-medium">9:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Friday - Saturday</span>
                      <span className="font-medium">Closed</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-4">
                    * UAE Time (GMT+4)
                  </p>
                </CardContent>
              </Card>

              {/* Service Region */}
              <div className="mt-6 p-6 bg-blue-50 rounded-lg">
                <h3 className="font-semibold mb-2">Service Region</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  We serve customers across the GCC region: UAE, Saudi Arabia, Oman, Qatar, 
                  Kuwait, and Bahrain with reliable delivery and technical support.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-center mb-8">Our Location</h2>
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <MapPin className="text-primary mx-auto mb-4" size={48} />
            <h3 className="text-xl font-semibold mb-2">Dubai South Business Park</h3>
            <p className="text-gray-600 mb-4">
              Strategically located in Dubai South for optimal access to GCC markets
            </p>
            <p className="text-sm text-gray-500">
              {companyInfo.address.line1}, {companyInfo.address.line2}<br />
              {companyInfo.address.city}, {companyInfo.address.country}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
